/**
 * Partidas salvas — guardar a batalha no meio e voltar a ela depois.
 *
 * Um mapa de trinta e cinco por quarenta com dez unidades leva uma hora para
 * ser jogado até o fim. Sem salvar, fechar a aba custava a hora inteira, e o
 * Modo História pedia que se vencesse cada capítulo numa sentada só.
 *
 * O que se guarda é o ESTADO da partida (`serialize` de @oz/rules) mais o
 * pouco que a tela precisa para remontá-la: qual mapa, qual capítulo, quem é
 * quem na hora de desenhar. O mapa em si fica de fora — ele não muda durante a
 * partida, e recarregá-lo do servidor é mais barato do que guardar quarenta
 * mil números em cada espaço de gravação.
 *
 * Mora no navegador (`localStorage`), e não no servidor, por duas razões: a
 * escaramuça é jogável sem conta, e um estado de partida é grande demais para
 * ir e voltar a cada turno numa gravação automática. O que o servidor guarda
 * continua sendo o que é dele — o esquadrão e o progresso da campanha.
 */

import { deserialize, type MatchState, type SerializedState } from '@oz/rules';
import type { GameMap } from '@oz/rules';
import type { UnitPresentation } from './setup.js';

/** Onde cada espaço de gravação mora dentro do `localStorage`. */
const PREFIX = 'oz-emblem:save:';

/**
 * Quantos espaços de gravação manual existem.
 *
 * Três, como num console: um para a campanha em curso, um para a escaramuça
 * que se quer retomar, e um de reserva para quem não quer escolher.
 */
export const SAVE_SLOTS = 3;

/** O espaço da gravação automática, escrito a cada turno. */
export const AUTO_SLOT = 'auto';

export type SlotId = number | typeof AUTO_SLOT;

/** O capítulo, quando a partida salva é de campanha. Ver `Chapter`. */
export interface SavedChapter {
  readonly id: number;
  readonly title: string;
  readonly campaignSlug: string;
  readonly ordinal?: number | undefined;
}

export interface SaveGame {
  /** Sobe quando o formato muda de um jeito que as gravações antigas não leem. */
  readonly version: 1;
  readonly savedAt: string;
  readonly mode: 'escaramuca' | 'campanha';
  readonly mapId: number;
  readonly mapName: string;
  readonly seed: number;
  readonly difficulty: 'facil' | 'normal' | 'dificil';
  readonly turn: number;
  /** Quantos de cada lado ainda estão de pé — o que o espaço mostra na lista. */
  readonly allies: number;
  readonly enemies: number;
  readonly state: SerializedState;
  /**
   * Quem é quem na hora de desenhar.
   *
   * Vai junto, e não é recalculado: o elenco de uma escaramuça sai de um
   * sorteio, e o de um capítulo sai do esquadrão como ele estava no dia da
   * gravação. Remontar qualquer um dos dois no ato de carregar devolveria
   * outras pessoas para as mesmas casas.
   */
  readonly presentation: readonly (readonly [number, UnitPresentation])[];
  readonly chapter?: SavedChapter | undefined;
  /** As conversas de recrutamento ainda pendentes. Ver `RecruitBook`. */
  readonly recruitLines?: readonly (readonly [number, string])[] | undefined;
}

/** O resumo de um espaço, para a tela de continuar. */
export interface SaveSummary {
  readonly slot: SlotId;
  readonly save: SaveGame;
}

function keyOf(slot: SlotId): string {
  return `${PREFIX}${slot}`;
}

function storage(): Storage | null {
  try {
    // Navegador em janela anônima com dados de site bloqueados lança AQUI, e
    // não na leitura: o acesso à propriedade é o que estoura.
    return window.localStorage;
  } catch {
    return null;
  }
}

/** A gravação de um espaço, ou `null` se ele está vazio ou ilegível. */
export function readSave(slot: SlotId): SaveGame | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(keyOf(slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SaveGame;
    // Uma gravação de outro formato é tratada como espaço vazio: melhor do que
    // abrir uma partida com metade dos campos faltando.
    if (parsed?.version !== 1 || !parsed.state) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSave(slot: SlotId, save: SaveGame): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(keyOf(slot), JSON.stringify(save));
    return true;
  } catch {
    // Cota estourada é o caso comum. Quem salva merece saber que não salvou.
    return false;
  }
}

export function deleteSave(slot: SlotId): void {
  storage()?.removeItem(keyOf(slot));
}

/** Todos os espaços ocupados, o automático primeiro. */
export function listSaves(): readonly SaveSummary[] {
  const out: SaveSummary[] = [];
  const auto = readSave(AUTO_SLOT);
  if (auto) out.push({ slot: AUTO_SLOT, save: auto });
  for (let i = 1; i <= SAVE_SLOTS; i++) {
    const save = readSave(i);
    if (save) out.push({ slot: i, save });
  }
  return out;
}

/** Existe alguma partida para continuar? */
export function hasAnySave(): boolean {
  return listSaves().length > 0;
}

/** A gravação mais recente desta campanha, se houver uma. */
export function latestChapterSave(campaignSlug: string, chapterId?: number): SaveSummary | null {
  const candidates = listSaves().filter(
    ({ save }) =>
      save.mode === 'campanha' &&
      save.chapter?.campaignSlug === campaignSlug &&
      (chapterId === undefined || save.chapter?.id === chapterId),
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((newest, entry) =>
    entry.save.savedAt > newest.save.savedAt ? entry : newest,
  );
}

/** O estado da partida de volta, com o mapa recarregado por quem chama. */
export function restoreState(save: SaveGame, map: GameMap): MatchState {
  return deserialize(save.state, map);
}

/** Como o espaço se apresenta na lista: "Capítulo 2 — Oásis · turno 7". */
export function saveLabel(save: SaveGame): string {
  const where = save.chapter ? save.chapter.title : save.mapName;
  return `${where} · turno ${save.turn}`;
}

/** "hoje às 21:04", "11/09 às 21:04" — o bastante para escolher entre dois. */
export function saveWhen(save: SaveGame): string {
  const when = new Date(save.savedAt);
  if (Number.isNaN(when.getTime())) return '';
  const time = when.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const sameDay =
    when.getDate() === today.getDate() &&
    when.getMonth() === today.getMonth() &&
    when.getFullYear() === today.getFullYear();
  return sameDay
    ? `hoje às ${time}`
    : `${when.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${time}`;
}
